export const COLS = 10;
export const ROWS = 14;

const DEFAULT_COLORS = ['#4ec9b0', '#569cd6', '#ce9178', '#c586c0', '#dcdcaa', '#9cdcfe'];

export function createEmptyBoard() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

export function collides(board, width, height, x, y) {
  for (let row = 0; row < height; row += 1) {
    for (let col = 0; col < width; col += 1) {
      const bx = x + col;
      const by = y + row;
      if (bx < 0 || bx >= COLS || by >= ROWS) {
        return true;
      }
      if (by >= 0 && board[by][bx]) {
        return true;
      }
    }
  }
  return false;
}

export function getDropY(board, width, height, x) {
  let y = -height;
  while (!collides(board, width, height, x, y + 1)) {
    y += 1;
  }
  return y;
}

export function lockPieceOnBoard(board, piece) {
  const newBoard = board.map((row) => [...row]);
  const { x, y, width, height, label, color, id } = piece;
  const pieceKey = piece.pieceKey || `${id}-${x}-${y}-${Math.random().toString(36).slice(2, 8)}`;

  for (let row = 0; row < height; row += 1) {
    for (let col = 0; col < width; col += 1) {
      const by = y + row;
      const bx = x + col;
      if (by >= 0 && by < ROWS && bx >= 0 && bx < COLS) {
        newBoard[by][bx] = { id, label, color, pieceKey };
      }
    }
  }

  return newBoard;
}

function computeMetrics(board) {
  const heights = Array(COLS).fill(0);
  let holes = 0;

  for (let col = 0; col < COLS; col += 1) {
    let foundBlock = false;
    for (let row = 0; row < ROWS; row += 1) {
      if (board[row][col]) {
        foundBlock = true;
        heights[col] = ROWS - row;
      } else if (foundBlock) {
        holes += 1;
      }
    }
  }

  const aggregateHeight = heights.reduce((sum, height) => sum + height, 0);
  const bumpiness = heights.reduce((sum, height, index) => {
    if (index === 0) {
      return 0;
    }
    return sum + Math.abs(height - heights[index - 1]);
  }, 0);

  return { aggregateHeight, holes, bumpiness };
}

export function scoreBoard(board) {
  const { aggregateHeight, holes, bumpiness } = computeMetrics(board);
  return aggregateHeight * 10 + holes * 50 + bumpiness * 5;
}

function getOrientations(competence) {
  if (competence.width === competence.height) {
    return [{ width: competence.width, height: competence.height, rotated: false }];
  }

  return [
    { width: competence.width, height: competence.height, rotated: false },
    { width: competence.height, height: competence.width, rotated: true },
  ];
}

export function findBestPlacement(board, competence) {
  let best = null;
  let bestScore = Infinity;

  for (const orientation of getOrientations(competence)) {
    for (let x = 0; x <= COLS - orientation.width; x += 1) {
      const y = getDropY(board, orientation.width, orientation.height, x);
      if (y < 0) {
        continue;
      }

      const tempPiece = {
        ...competence,
        ...orientation,
        x,
        y,
      };
      const tempBoard = lockPieceOnBoard(board, tempPiece);
      const score = scoreBoard(tempBoard);

      if (score < bestScore) {
        bestScore = score;
        best = { x, y, ...orientation };
      }
    }
  }

  return best;
}

export function canSpawnAny(board, competences) {
  return competences.some((competence) => {
    for (const orientation of getOrientations(competence)) {
      for (let x = 0; x <= COLS - orientation.width; x += 1) {
        if (!collides(board, orientation.width, orientation.height, x, 0)) {
          return true;
        }
      }
    }
    return false;
  });
}

export function pickRandomCompetence(competences) {
  return competences[Math.floor(Math.random() * competences.length)];
}

export function competenceColor(competence, index) {
  return competence.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

export function blocksFromBoard(board) {
  const visited = Array.from({ length: ROWS }, () => Array(COLS).fill(false));
  const blocks = [];

  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      const cell = board[row][col];
      if (!cell || visited[row][col]) {
        continue;
      }

      const { pieceKey } = cell;
      let width = 1;
      while (
        col + width < COLS
        && board[row][col + width]?.pieceKey === pieceKey
        && !visited[row][col + width]
      ) {
        width += 1;
      }

      let height = 1;
      let canGrow = true;
      while (canGrow && row + height < ROWS) {
        for (let dx = 0; dx < width; dx += 1) {
          if (
            board[row + height][col + dx]?.pieceKey !== pieceKey
            || visited[row + height][col + dx]
          ) {
            canGrow = false;
            break;
          }
        }
        if (canGrow) {
          height += 1;
        }
      }

      for (let dy = 0; dy < height; dy += 1) {
        for (let dx = 0; dx < width; dx += 1) {
          visited[row + dy][col + dx] = true;
        }
      }

      blocks.push({
        id: pieceKey,
        label: cell.label,
        color: cell.color,
        x: col,
        y: row,
        width,
        height,
      });
    }
  }

  return blocks;
}

export function buildStaticBoard(competences) {
  let board = createEmptyBoard();
  const shuffled = [...competences].sort(() => Math.random() - 0.5);

  for (const competence of shuffled) {
    const placement = findBestPlacement(board, competence);
    if (!placement) {
      continue;
    }

    board = lockPieceOnBoard(board, {
      ...competence,
      color: competence.color || DEFAULT_COLORS[0],
      ...placement,
    });
  }

  return board;
}
